/**
 * Atendimento — leitura e abertura de chamados pelo CRM.
 *
 * O RLS de `support_tickets` já limita ao clube de quem consulta
 * (`has_permission(..., 'support.view')`), então as queries não filtram por
 * club_id: filtrar no cliente daria a falsa impressão de que o isolamento mora
 * aqui. O `clubId` entra só na query key, para o cache não vazar entre clubes
 * quando o usuário troca de clube sem recarregar a página.
 *
 * Os prazos de SLA NÃO são enviados na criação — quem carimba é a trigger
 * `tg_support_ticket_apply_sla` (migration 20260810144203). O front nem precisa
 * conhecer a política.
 */
import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { qk } from '@pps/query-keys';
import { supabase } from '@/integrations/supabase/client';
import { useClub } from '@/hooks/useClub';

const BUCKET = 'support-attachments';

/** Extensão do arquivo no bucket, por tipo aceito. */
const EXTENSAO: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

export type SupportStatus =
  | 'aberto'
  | 'em_andamento'
  | 'aguardando_cliente'
  | 'resolvido'
  | 'fechado';

export type SupportPrioridade = 'baixa' | 'normal' | 'alta' | 'critica';

export interface SupportTicket {
  id: string;
  /** Protocolo global, OCOR-2026-0042 — o que a PPS usa para achar o chamado. */
  protocolo: string;
  /** Numero dentro do clube (1, 2, 3...) — o que faz sentido para quem abriu. */
  protocolo_clube: number;
  club_id: string;
  opened_by: string;
  categoria: number;
  prioridade: SupportPrioridade;
  status: SupportStatus;
  titulo: string;
  descricao: string;
  rota: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  sla_first_response_due_at: string | null;
  sla_resolution_due_at: string | null;
  sla_first_response_breached: boolean;
  sla_resolution_breached: boolean;
  video_slug: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  author_id: string | null;
  author_side: 'clube' | 'pps' | 'ia';
  body: string;
  created_at: string;
}

export function useSupportTickets(status?: SupportStatus | null) {
  const { club } = useClub();
  return useQuery({
    queryKey: qk.support.list(club?.id, { status: status ?? null }),
    enabled: !!club?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<SupportTicket[]> => {
      let q = supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false });
      if (status) q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SupportTicket[];
    },
  });
}

export function useSupportMessages(ticketId: string | null) {
  return useQuery({
    queryKey: qk.support.messages(ticketId),
    enabled: !!ticketId,
    staleTime: 15_000,
    queryFn: async (): Promise<SupportMessage[]> => {
      const { data, error } = await supabase
        .from('support_ticket_messages')
        .select('id, ticket_id, author_id, author_side, body, created_at')
        .eq('ticket_id', ticketId!)
        // NOTA INTERNA NUNCA APARECE AQUI, e o filtro é explícito.
        //
        // A versão anterior omitia este `.eq` de propósito, argumentando que a
        // policy de SELECT já resolvia e que filtrar no front sugeriria que a
        // proteção mora aqui. O argumento estava errado num ponto: policies do
        // Postgres são PERMISSIVE, então se SOMAM. Quem é staff do clube cai na
        // "Club staff read own club messages" (`NOT internal`) e nunca vê nota
        // interna — mas quem também é ADMIN DA PLATAFORMA cai na
        // "Admins read all messages", que libera tudo, e passava a ver nota
        // interna dentro do CRM. Foi o que o PO viu, com a conta dele.
        //
        // Esta tela é A VISÃO DO CLUBE. Ela deve mostrar o que o clube vê
        // independentemente de quem está logado, e é isso que este filtro diz.
        // O RLS continua sendo a fronteira de segurança; aqui é semântica.
        .eq('internal', false)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SupportMessage[];
    },
  });
}

export interface SupportAnexo {
  id: string;
  /** NULL = anexo da abertura (o print). Os demais pertencem a uma mensagem. */
  message_id: string | null;
  mime: string;
  /** URL assinada, válida por 1h. `null` se a assinatura falhou. */
  url: string | null;
}

/** Validade da URL assinada. Uma hora cobre a leitura da thread com folga. */
const VALIDADE_URL_SEGUNDOS = 60 * 60;

/**
 * Anexos do chamado, já com URL assinada.
 *
 * Uma consulta por CHAMADO e não por mensagem: uma thread tem dezenas de
 * mensagens e no máximo um punhado de anexos, então N consultas assinariam
 * quase sempre zero arquivo. A tela agrupa por `message_id` em memória.
 *
 * `createSignedUrls` (plural) assina o lote inteiro numa chamada; assinar uma
 * a uma seria uma requisição por arquivo.
 */
export function useAnexosDoChamado(ticketId: string | null) {
  return useQuery({
    queryKey: qk.support.attachments(ticketId),
    enabled: !!ticketId,
    // Menor que a validade da URL, senão a tela serviria link expirado do cache.
    staleTime: 30 * 60_000,
    queryFn: async (): Promise<SupportAnexo[]> => {
      const { data, error } = await supabase
        .from('support_ticket_attachments')
        .select('id, message_id, path, mime, created_at')
        .eq('ticket_id', ticketId!)
        .order('created_at', { ascending: true });
      if (error) throw error;

      const linhas = (data ?? []) as {
        id: string;
        message_id: string | null;
        path: string;
        mime: string;
      }[];
      if (linhas.length === 0) return [];

      const { data: assinadas } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(
          linhas.map((l) => l.path),
          VALIDADE_URL_SEGUNDOS,
        );
      const porPath = new Map(
        (assinadas ?? []).map((a) => [a.path ?? '', a.signedUrl ?? null]),
      );

      return linhas.map((l) => ({
        id: l.id,
        message_id: l.message_id,
        mime: l.mime,
        url: porPath.get(l.path) ?? null,
      }));
    },
  });
}

export interface AbrirChamadoInput {
  categoria: number;
  prioridade: SupportPrioridade;
  titulo: string;
  descricao: string;
  rota: string;
  contexto: Record<string, string | number>;
  /** Print da tela. Opcional: falha de captura não impede abrir o chamado. */
  print?: Blob | null;
  /** Tipo real da imagem — captura automática é PNG, anexo do usuário pode não ser. */
  printMime?: string | null;
}

export function useAbrirChamado() {
  const { club } = useClub();

  return useDomainMutation<SupportTicket, Error, AbrirChamadoInput>({
    mutationFn: async (input) => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!club?.id || !userId) throw new Error('sem_clube_ou_usuario');

      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          club_id: club.id,
          opened_by: userId,
          categoria: input.categoria,
          prioridade: input.prioridade,
          titulo: input.titulo,
          descricao: input.descricao,
          rota: input.rota,
          contexto: input.contexto,
        })
        .select('*')
        .single();
      if (error) throw error;
      const criado = ticket as SupportTicket;

      // A descrição também vira a primeira mensagem da thread. Sem isso a
      // conversa começaria com o relato fora dela, e o histórico ficaria
      // pela metade.
      const { error: msgErr } = await supabase
        .from('support_ticket_messages')
        .insert({
          ticket_id: criado.id,
          author_id: userId,
          author_side: 'clube',
          body: input.descricao,
        });
      if (msgErr) throw msgErr;

      // O anexo é acessório: se o upload falhar, o chamado continua aberto e
      // válido. Perder o print é muito melhor que perder o relato.
      //
      // O tipo vem de fora porque a imagem tanto pode ser a captura automática
      // (sempre PNG) quanto um arquivo escolhido pelo usuário (JPEG, WEBP).
      // Gravar tudo como PNG faria o navegador receber um Content-Type que não
      // bate com os bytes na hora de abrir a URL assinada.
      if (input.print) {
        const mime = input.printMime ?? 'image/png';
        const path = `${club.id}/${criado.id}/tela.${EXTENSAO[mime] ?? 'png'}`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, input.print, { contentType: mime, upsert: true });
        if (!upErr) {
          await supabase.from('support_ticket_attachments').insert({
            ticket_id: criado.id,
            path,
            mime,
            size_bytes: input.print.size,
            created_by: userId,
          });
        }
      }

      return criado;
    },
    invalidates: [qk.support.all],
    toast: false, // o diálogo faz o toast localizado
    tags: { feature: 'crm', action: 'support_open_ticket' },
  });
}

export interface ResponderChamadoInput {
  ticketId: string;
  clubId: string;
  body: string;
  /** Imagem anexada à resposta. Opcional. */
  anexo?: Blob | null;
  anexoMime?: string | null;
}

/**
 * Resposta do CLIENTE na thread.
 *
 * Não mexe no status. Quem move o ticket de volta para 'em_andamento' quando o
 * cliente responde é o lado da PPS (F4): status é estado do atendimento, e
 * deixar o cliente alterá-lo abriria caminho para "reabrir" um chamado
 * resolvido só respondendo "obrigado".
 *
 * ANEXO: o clube só conseguia mandar imagem na ABERTURA — depois disso, se
 * precisasse mostrar outra tela, não tinha caminho. Agora a mensagem é inserida
 * com `.select('id')` justamente para amarrar o arquivo a ela pelo `message_id`
 * (migration 20260811141429); sem o id de volta o anexo ficaria solto no
 * chamado, como o print de abertura.
 *
 * O nome do arquivo leva o `message_id`, e não um nome fixo: duas respostas com
 * anexo na mesma thread sobrescreveriam uma à outra num caminho fixo — foi
 * exatamente o que o `tela.png` da abertura fez enquanto era o único anexo
 * possível.
 *
 * Falha de upload NÃO derruba a resposta, mesma regra da abertura: perder a
 * imagem é ruim, perder o texto é pior. O caller é avisado pelo retorno.
 */
export function useResponderChamado() {
  return useDomainMutation<{ anexoFalhou: boolean }, Error, ResponderChamadoInput>({
    mutationFn: async ({ ticketId, clubId, body, anexo, anexoMime }) => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('sem_usuario');

      const { data: msg, error } = await supabase
        .from('support_ticket_messages')
        .insert({
          ticket_id: ticketId,
          author_id: userId,
          author_side: 'clube',
          body,
        })
        .select('id')
        .single();
      if (error) throw error;

      if (!anexo) return { anexoFalhou: false };

      const mime = anexoMime ?? 'image/png';
      const path = `${clubId}/${ticketId}/msg-${msg.id}.${EXTENSAO[mime] ?? 'png'}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, anexo, { contentType: mime, upsert: true });
      if (upErr) return { anexoFalhou: true };

      const { error: anexoErr } = await supabase
        .from('support_ticket_attachments')
        .insert({
          ticket_id: ticketId,
          message_id: msg.id,
          path,
          mime,
          size_bytes: anexo.size,
          created_by: userId,
        });
      return { anexoFalhou: !!anexoErr };
    },
    invalidates: [qk.support.all],
    toast: false, // a thread trata o erro no try/catch do caller
    tags: { feature: 'crm', action: 'support_reply' },
  });
}
